import React from "react";

import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import { useKeyboardShortcut } from "@/hooks/use-keyboard-shortcut";
import { useDebugStore } from "@/stores/preferences/debug-store";

import StyleCustomizer from "../preferences/style-customizer/style-customizer";
import { queryClient } from "./tanstack-query-provider";

declare global {
  interface Window {
    /**
     * Toggles the developer tools (React Query Devtools and TanStack Router Devtools).
     **/
    toggleDevTools: () => void;
  }
}

const TanStackRouterDevtools =
  process.env.NODE_ENV === "production"
    ? () => null
    : React.lazy(() =>
        import("@tanstack/react-router-devtools").then((res) => ({
          default: res.TanStackRouterDevtools,
        }))
      );

function TailwindBreakpointIndicator() {
  return (
    <div className="fixed top-2 left-2 z-50 px-3 py-1 text-white text-xs font-semibold bg-black bg-opacity-70 rounded-md shadow-md">
      <span className="block sm:hidden bg-red-500 px-2 py-1 rounded">XS</span>
      <span className="hidden sm:block md:hidden bg-orange-500 px-2 py-1 rounded">SM</span>
      <span className="hidden md:block lg:hidden bg-yellow-500 px-2 py-1 rounded">MD</span>
      <span className="hidden lg:block xl:hidden bg-green-500 px-2 py-1 rounded">LG</span>
      <span className="hidden xl:block 2xl:hidden bg-blue-500 px-2 py-1 rounded">XL</span>
      <span className="hidden 2xl:block bg-purple-500 px-2 py-1 rounded">2XL+</span>
    </div>
  );
}

/**
 * DebugProvider Component
 *
 * This component provides debugging tools for development purposes.
 * It renders the TanStack Router Devtools and React Query Devtools.
 *
 * The component is wrapped in a React.Suspense to handle the lazy-loaded TanStackRouterDevtools.
 *
 * @returns A React element containing the debug tools
 */
function DebugProvider() {
  const { devToolsMounted, toggleDevTools } = useDebugStore();

  window.toggleDevTools = () => {
    toggleDevTools();
    console.log("%cToggled dev tools", "color: #bada55; font-size: 16px; font-weight: bold;");
  };

  useKeyboardShortcut({ key: "d", ctrl: true, shift: true }, toggleDevTools);

  if (import.meta.env.PROD) return null; // Don't render in production
  if (!devToolsMounted) return null;

  return (
    <React.Suspense>
      <style>{`
      .tsqd-open-btn-container{
        pointer-events: auto;
      }
        
      .tsqd-parent-container {
        pointer-events: auto;
      }
    `}</style>
      <TanStackRouterDevtools toggleButtonProps={{ style: { right: "70px" } }} position="top-right" initialIsOpen={false} />
      <ReactQueryDevtools initialIsOpen={false} client={queryClient} buttonPosition="top-right" />
      <TailwindBreakpointIndicator />

      <StyleCustomizer />
    </React.Suspense>
  );
}

export default DebugProvider;
